import { Link } from "@tanstack/react-router";

import { CardArtigo } from "@/components/blog/BlogListing";
import { Markdown } from "@/components/blog/Markdown";
import { OutlineButton } from "@/components/OutlineButton";
import { sobreLink } from "@/components/site-nav";
import type { Artigo } from "@/lib/artigo.functions";
import type { SiteData } from "@/lib/site-data.functions";

const AVATAR = "/dr-thiago-viana-avatar.png";
const AVISO = "Este conteúdo é educativo e não substitui consulta.";

/** Data no formato do leitor. Fuso fixo para servidor e navegador baterem. */
function formatarData(valor: string | null): string | null {
  if (!valor) return null;
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return null;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(data);
}

function Breadcrumb({ trilha }: { trilha: Array<{ nome: string; path: string }> }) {
  return (
    <nav aria-label="Você está aqui">
      <ol className="t-label-nav flex flex-wrap items-center gap-[calc(var(--space-16)/2)] uppercase text-text-faint">
        {trilha.map((item, indice) => (
          <li key={item.path} className="flex items-center gap-[calc(var(--space-16)/2)]">
            {indice > 0 ? <span aria-hidden="true">›</span> : null}
            <span>{item.nome}</span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * Bloco "quem escreve".
 *
 * A bio vem de doctor.bio_short. Enquanto o campo estiver vazio, o bloco mostra
 * apenas as especialidades dos RQEs: nunca se escreve aqui instituição, título
 * ou curso que não esteja na tabela doctor.
 */
function QuemEscreve({ doctor, bio }: { doctor: SiteData["doctor"]; bio: string | null }) {
  if (!doctor?.name) return null;
  const especialidades = (doctor.rqes ?? [])
    .map((rqe) => rqe.especialidade)
    .filter((item): item is string => Boolean(item && item.trim()));
  const texto = bio?.trim() || (especialidades.length ? especialidades.join(" · ") : null);

  return (
    <section className="artigo-autor">
      <img
        src={AVATAR}
        alt={`Retrato do ${doctor.name}`}
        width={112}
        height={112}
        loading="lazy"
        className="artigo-autor-foto"
      />
      <div className="artigo-autor-conteudo">
        <p className="artigo-autor-rotulo t-label-block uppercase">Quem escreve</p>
        <p className="artigo-autor-nome t-heading-sm">{doctor.name}</p>
        {doctor.crm ? <p className="artigo-autor-crm t-card-meta">{doctor.crm}</p> : null}
        {texto ? <p className="artigo-autor-bio t-card-excerpt">{texto}</p> : null}
        <Link
          {...sobreLink}
          className="artigo-autor-link t-link-inline inline-flex items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-dark"
        >
          Conhecer o Dr. Thiago →
        </Link>
      </div>
    </section>
  );
}

/** Domínio de exibição da fonte. Se a URL for inválida, não se mostra nada. */
function dominio(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/**
 * Linha "Fontes consultadas". As referências continuam no HTML entregue pelo
 * servidor e ficam escondidas apenas pelo <details> fechado, no estilo das
 * páginas de saúde grandes. Não usa título de seção: a hierarquia de títulos do
 * artigo termina no FAQ.
 */
function Fontes({ referencias }: { referencias: Artigo["referencias"] }) {
  if (!referencias.length) return null;

  return (
    <details className="artigo-fontes">
      <summary className="t-label-block uppercase text-text-faint">
        <span>Fontes consultadas ({referencias.length})</span>
        <span className="frow-sinal" aria-hidden="true" />
      </summary>
      <ol className="artigo-fontes-lista t-card-meta">
        {referencias.map((ref) => {
          const site = dominio(ref.url);
          return (
            <li key={ref.titulo}>
              {ref.url ? (
                <a
                  href={ref.url}
                  target="_blank"
                  rel="noopener"
                  className="artigo-fontes-link"
                >
                  {ref.titulo}
                </a>
              ) : (
                <span className="artigo-fontes-titulo">{ref.titulo}</span>
              )}
              {site ? <span className="artigo-fontes-site"> ({site})</span> : null}
            </li>
          );
        })}
      </ol>
    </details>
  );
}

function CtaContextual({ artigo }: { artigo: Artigo }) {
  const destino = artigo.related_treatment_path?.trim() || null;
  const chamada = artigo.categoria
    ? `Tratamento de ${artigo.categoria.label.toLowerCase()} com o Dr. Thiago Viana.`
    : null;

  return (
    <section className="artigo-cta">
      <img className="artigo-cta-fundo" src="/img/cta-final-fundo.webp" alt="" width={1440} height={650} loading="lazy" />
      <span className="artigo-cta-veu" aria-hidden="true" />
      <div className="artigo-cta-conteudo">
        <div className="artigo-cta-texto">
          <p className="artigo-cta-aviso t-card-excerpt">{AVISO}</p>
          {destino && chamada ? (
            <p className="artigo-cta-titulo t-heading-sm">{chamada}</p>
          ) : null}
        </div>
        {destino ? (
          <OutlineButton to={destino} className="shrink-0 border-neutral-white text-neutral-white">
            Ver tratamento
          </OutlineButton>
        ) : null}
      </div>
    </section>
  );
}

export type ArticlePageProps = {
  artigo: Artigo;
  doctor: SiteData["doctor"];
  bio: string | null;
  trilha: Array<{ nome: string; path: string }>;
};

export function ArticlePage({ artigo, doctor, bio, trilha }: ArticlePageProps) {
  const h1 = artigo.h1 || artigo.title || "";
  const publicado = formatarData(artigo.published_at);
  const revisado = formatarData(artigo.reviewed_at);
  const meta = [
    publicado ? `Publicado em ${publicado}` : null,
    revisado ? `Revisado em ${revisado}` : null,
    artigo.reading_minutes ? `Leitura de ${artigo.reading_minutes} min` : null,
  ].filter(Boolean) as string[];

  return (
    <main className="bg-background-white">
      <div className="mx-auto w-full max-w-[880px] px-16 md:px-48 lg:px-0">
        <header className="flex flex-col gap-[calc(var(--space-16)+2px)] pb-48 pt-[calc(var(--space-64)+8px)]">
          <Breadcrumb trilha={trilha} />
          <p className="t-label-nav uppercase text-gold-dark">
            {artigo.categoria ? `Artigo · ${artigo.categoria.label}` : "Artigo"}
          </p>
          <h1 className="t-article-h1 text-text-body">{h1}</h1>
          {artigo.subtitle ? (
            <p className="t-article-lead text-text-muted">{artigo.subtitle}</p>
          ) : null}
          {doctor?.name ? (
            <div className="flex items-center gap-16">
              <img
                src={AVATAR}
                alt={`Retrato do ${doctor.name}`}
                width={44}
                height={44}
                className="h-[44px] w-[44px] rounded-full object-cover"
              />
              <div className="flex flex-col gap-[calc(var(--space-16)/4)]">
                <p className="t-byline text-text-body">
                  {[doctor.name, doctor.crm].filter(Boolean).join(", ")}
                </p>
                {meta.length ? (
                  <p className="t-byline-meta uppercase text-text-faint">{meta.join(" · ")}</p>
                ) : null}
              </div>
            </div>
          ) : null}
        </header>

        {artigo.capa ? (
          <img
            src={artigo.capa.path}
            alt={artigo.capa.alt}
            width={artigo.capa.width ?? 880}
            height={artigo.capa.height ?? 440}
            className="h-[440px] w-full rounded-article bg-surface-image object-cover"
          />
        ) : null}

        <div className="flex flex-col gap-28 py-64">
          {artigo.direct_answer ? (
            <section className="border-l-[3px] border-gold-flat bg-background-cream px-[calc(var(--space-16)+8px)] py-28">
              <p className="t-label-block uppercase text-gold-dark">Em resumo</p>
              <p className="t-summary mt-[calc(var(--space-16)/2)] text-text-body">
                {artigo.direct_answer}
              </p>
            </section>
          ) : null}

          {artigo.body ? <Markdown texto={artigo.body} links={artigo.links} /> : null}

          {artigo.faq.length ? (
            <section className="artigo-faq">
              <h2 className="t-article-h2 text-text-body">Perguntas frequentes</h2>
              {artigo.faq.map((item, indice) => (
                <details className="frow" key={item.pergunta}>
                  <summary>
                    <span className="frow-n">{String(indice + 1).padStart(2, "0")}</span>
                    <h3>{item.pergunta}</h3>
                    <span className="frow-sinal" aria-hidden="true" />
                  </summary>
                  <div className="frow-corpo">
                    <p>{item.resposta}</p>
                  </div>
                </details>
              ))}
            </section>
          ) : null}

          <QuemEscreve doctor={doctor} bio={bio} />
          <Fontes referencias={artigo.referencias} />
          <CtaContextual artigo={artigo} />
        </div>

        {artigo.relacionados.length ? (
          <section className="flex flex-col gap-28 pb-64">
            <h2 className="t-display-sm text-text-body">Artigos relacionados</h2>
            <div className="grid grid-cols-1 gap-28 md:grid-cols-2 lg:grid-cols-3">
              {artigo.relacionados.map((post) => (
                <CardArtigo key={post.slug} post={post} />
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </main>
  );
}

export default ArticlePage;
